import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DollarSign, Clock, Percent, FileText, ArrowRight, Shield, Building2, CreditCard, AlertTriangle, Calendar, Globe, Landmark, Maximize2, Minimize2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export interface FinancingOptionData {
  title: string;
  details: string;
  // Loan Type & Terms
  lenderType?: string;
  loanProductType?: string;
  // Commercial Terms
  loanAmountOffered?: number;
  loanTermLengthMonths?: number;
  interestRateType?: string;
  interestRateValue?: number;
  interestRateMarginBps?: number | null;
  interestRateFloor?: number | null;
  amortizationType?: string;
  prepaymentPenalty?: string;
  // Structured Fees
  arrangementFeePercent?: number | null;
  drawFeePercentABL?: number | null;
  exitFeePercent?: number | null;
  feesList?: { description: string; amount: number; type: string }[];
  // Covenants, Ratios & Collateral
  maintenanceCovenantsRequired?: boolean;
  netDebtToEBITDAMaxAllowed?: number | null;
  dscrMinimumRequired?: number;
  dscrCalculationBase?: string;
  dividendRestrictions?: string;
  collateralRequired?: boolean;
  collateralRequirements?: string[];
  personalGuaranteeRequired?: boolean;
  realEstateConditioned?: boolean;
  isABLOffer?: boolean;
  ABL_AR_EligiblityPercent?: number | null;
  ABL_Inventory_EligiblityPercent?: number | null;
  ABL_Cash_Sweep?: boolean;
  // Subordinated Debt
  isSubordinatedDebt?: boolean;
  subordinationToSeniorDebt?: boolean;
  interestGracePeriodMonths?: number | null;
  balloonPaymentYear?: number | null;
  balloonPaymentAmount?: number | null;
  // Credit Requirements & International
  creditScoreRequired?: boolean;
  creditScoreMinimum?: number | null;
  acceptForeignCreditScores?: boolean;
  foreignCreditScoreMinimum?: number | null;
  acceptedForeignCreditCountries?: string[];
  acceptForeignCollateral?: boolean;
  foreignCollateralRequirements?: string[];
  // Timeline
  estimatedClosingTimelineDays?: number;
  offerExpirationDate?: string;
}

interface FinancingOptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  option: FinancingOptionData | null;
  listingTitle: string;
}

const T = {
  en: {
    offerDetails: 'Financing Offer Details',
    offerDesc: 'Pre-financing offer for this business',
    anonymousLender: 'Anonymous Lender',
    note: 'The identity of the lender will be revealed after signing a Letter of Intent (LOI).',
    interested: "I'm Interested",
    close: 'Close',
    interestConfirm: 'Interest registered! Our team will contact you shortly.',
    // Sections
    sLoanType: 'Loan Type & Terms',
    sCommercial: 'Commercial Terms',
    sFees: 'Structured Fees',
    sCovenants: 'Covenants, Ratios & Collateral',
    sCredit: 'Credit Requirements & International',
    sTimeline: 'Timeline & Final Review',
    sSubDebt: 'Subordinated Debt',
    sABL: 'ABL Details',
    // Labels
    lenderType: 'Lender Type',
    productType: 'Loan Product Type',
    loanAmount: 'Loan Amount Offered',
    loanTerm: 'Term',
    rateType: 'Rate Type',
    baseRate: 'Base Rate',
    marginBps: 'Margin (bps)',
    floorRate: 'Floor Rate',
    amortization: 'Amortization Type',
    prepayment: 'Prepayment Penalty',
    arrangementFee: 'Arrangement Fee',
    ablDrawFee: 'ABL Draw Fee',
    exitFee: 'Exit Fee',
    additionalFees: 'Additional Fees',
    maintenanceCovenants: 'Maintenance Covenants',
    netDebtEBITDA: 'Max Net Debt / EBITDA',
    minDSCR: 'Minimum DSCR',
    dscrBase: 'DSCR Calculation Base',
    dividendRestrictions: 'Dividend Restrictions',
    collateral: 'Collateral Required',
    collateralTypes: 'Collateral Types',
    personalGuarantee: 'Personal Guarantee (PG)',
    realEstateMortgage: 'Conditioned on Real Estate Mortgage',
    ablAR: 'A/R Eligibility',
    ablInventory: 'Inventory Eligibility',
    ablSweep: 'Cash Sweep',
    subordinated: 'Subordinated Debt',
    subToSenior: 'Subordination to Senior Debt',
    gracePeriod: 'Interest Grace Period',
    balloonYear: 'Balloon Payment Year',
    balloonAmount: 'Balloon Payment Amount',
    creditScore: 'Credit Score Required',
    minCreditScore: 'Minimum Score',
    foreignCredit: 'Foreign Credit Accepted',
    foreignMinScore: 'Foreign Min Score',
    foreignCountries: 'Accepted Countries',
    foreignCollateral: 'Foreign Collateral Accepted',
    foreignCollateralTypes: 'Foreign Collateral Types',
    closingTimeline: 'Closing Timeline',
    offerExpires: 'Offer Expiration Date',
    months: 'months',
    days: 'days',
    yes: 'Yes',
    no: 'No',
    fixed: 'Fixed', floating: 'Floating', indexed: 'Indexed',
    linear: 'Linear', inFine: 'In Fine (Bullet)', customSchedule: 'Custom Schedule',
    notSpecified: 'Not specified',
    ofLoan: '% of loan amount',
    perDraw: '% per draw',
  },
  fr: {
    offerDetails: "Détails de l'offre de financement",
    offerDesc: 'Offre de pré-financement pour cette entreprise',
    anonymousLender: 'Prêteur anonyme',
    note: "L'identité du prêteur sera révélée après la signature d'une Lettre d'Intention (LOI).",
    interested: 'Je suis intéressé',
    close: 'Fermer',
    interestConfirm: 'Intérêt enregistré ! Notre équipe vous contactera sous peu.',
    sLoanType: 'Type de prêt et conditions',
    sCommercial: 'Conditions commerciales',
    sFees: 'Frais structurés',
    sCovenants: 'Engagements, ratios et garanties',
    sCredit: 'Exigences de crédit et international',
    sTimeline: 'Calendrier et revue finale',
    sSubDebt: 'Dette subordonnée',
    sABL: 'Détails ABL',
    lenderType: 'Type de prêteur',
    productType: 'Type de produit de prêt',
    loanAmount: 'Montant du prêt offert',
    loanTerm: 'Durée',
    rateType: 'Type de taux',
    baseRate: 'Taux de base',
    marginBps: 'Marge (bps)',
    floorRate: 'Taux plancher',
    amortization: "Type d'amortissement",
    prepayment: 'Pénalité de remboursement anticipé',
    arrangementFee: "Frais d'arrangement",
    ablDrawFee: 'Frais de tirage ABL',
    exitFee: 'Frais de sortie',
    additionalFees: 'Frais supplémentaires',
    maintenanceCovenants: 'Engagements de maintenance',
    netDebtEBITDA: 'Dette nette / EBITDA max',
    minDSCR: 'DSCR minimum',
    dscrBase: 'Base de calcul DSCR',
    dividendRestrictions: 'Restrictions de dividendes',
    collateral: 'Garantie requise',
    collateralTypes: 'Types de garanties',
    personalGuarantee: 'Garantie personnelle (PG)',
    realEstateMortgage: 'Conditionné à une hypothèque immobilière',
    ablAR: 'Éligibilité C/R',
    ablInventory: 'Éligibilité inventaire',
    ablSweep: 'Balayage de trésorerie',
    subordinated: 'Dette subordonnée',
    subToSenior: 'Subordination à la dette senior',
    gracePeriod: "Période de grâce d'intérêts",
    balloonYear: 'Année du paiement ballon',
    balloonAmount: 'Montant du paiement ballon',
    creditScore: 'Score de crédit requis',
    minCreditScore: 'Score minimum',
    foreignCredit: 'Crédit étranger accepté',
    foreignMinScore: 'Score min étranger',
    foreignCountries: 'Pays acceptés',
    foreignCollateral: 'Garantie étrangère acceptée',
    foreignCollateralTypes: 'Types de garanties étrangères',
    closingTimeline: 'Délai de clôture',
    offerExpires: "Date d'expiration de l'offre",
    months: 'mois',
    days: 'jours',
    yes: 'Oui',
    no: 'Non',
    fixed: 'Fixe', floating: 'Variable', indexed: 'Indexé',
    linear: 'Linéaire', inFine: 'In Fine (Bullet)', customSchedule: 'Échéancier personnalisé',
    notSpecified: 'Non spécifié',
    ofLoan: '% du montant du prêt',
    perDraw: '% par tirage',
  },
};

function fmt(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);
}

function Row({ label, value, icon: Icon }: { label: string; value: React.ReactNode; icon?: React.ComponentType<{ className?: string }> }) {
  return (
    <div className="flex items-start justify-between py-2">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        {Icon && <Icon className="h-3.5 w-3.5 flex-shrink-0" />}
        <span>{label}</span>
      </div>
      <div className="text-sm font-medium text-right max-w-[55%]">{value}</div>
    </div>
  );
}

function Section({ icon: Icon, title }: { icon: React.ComponentType<{ className?: string }>; title: string }) {
  return (
    <div className="flex items-center gap-2 pt-2 pb-1">
      <Icon className="h-4 w-4 text-[#b8924f]" />
      <h4 className="text-sm font-semibold text-[#2c3e50]">{title}</h4>
    </div>
  );
}

export function FinancingOptionDialog({ open, onOpenChange, option, listingTitle }: FinancingOptionDialogProps) {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const t = T[language as keyof typeof T] || T.en;
  if (!option) return null;

  const rateLabel = (v?: string) => {
    if (!v) return t.notSpecified;
    const map: Record<string, string> = { fixed: t.fixed, floating: t.floating, indexed: t.indexed };
    return map[v] || v;
  };
  const amortLabel = (v?: string) => {
    if (!v) return t.notSpecified;
    const map: Record<string, string> = { linear: t.linear, in_fine: t.inFine, custom_schedule: t.customSchedule };
    return map[v] || v;
  };
  const bool = (v?: boolean) => (v ? t.yes : t.no);

  const handleInterested = () => {
    toast({ title: t.interestConfirm });
    onOpenChange(false);
    navigate('/register/buyers');
  };

  const o = option;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`flex flex-col transition-all duration-300 ${isFullscreen ? 'max-w-[95vw] max-h-[95vh] w-[95vw] h-[95vh]' : 'max-w-lg max-h-[85vh]'}`}>
        <DialogHeader className="relative">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-[#b8924f]" />
              {t.offerDetails}
            </DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 mr-8"
              onClick={() => setIsFullscreen(!isFullscreen)}
              title={isFullscreen ? 'Reduce' : 'Expand'}
            >
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
          </div>
          <DialogDescription>{t.offerDesc}</DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-3 -mr-3">
          <div className="space-y-3 py-2">
            {/* Offer title banner */}
            <div className="p-4 bg-gradient-to-r from-[#b8924f]/10 to-[#d4af6a]/10 rounded-lg border border-[#b8924f]/20">
              <div className="text-lg font-semibold text-[#2c3e50]">{o.title}</div>
            </div>

            {/* 1. Loan Type & Terms */}
            {(o.lenderType || o.loanProductType) && (
              <>
                <Section icon={Landmark} title={t.sLoanType} />
                <div className="bg-muted/50 rounded-lg px-4 divide-y divide-border">
                  {o.lenderType && <Row label={t.lenderType} value={o.lenderType} />}
                  {o.loanProductType && <Row label={t.productType} value={o.loanProductType} />}
                </div>
              </>
            )}

            {/* 2. Commercial Terms */}
            <Section icon={Percent} title={t.sCommercial} />
            <div className="bg-muted/50 rounded-lg px-4 divide-y divide-border">
              {o.loanAmountOffered != null && <Row label={t.loanAmount} value={fmt(o.loanAmountOffered)} icon={DollarSign} />}
              {o.loanTermLengthMonths != null && <Row label={t.loanTerm} value={`${o.loanTermLengthMonths} ${t.months}`} />}
              {o.interestRateType && <Row label={t.rateType} value={rateLabel(o.interestRateType)} />}
              {o.interestRateValue != null && <Row label={t.baseRate} value={`${o.interestRateValue}%`} />}
              {o.interestRateMarginBps != null && <Row label={t.marginBps} value={`${o.interestRateMarginBps} bps`} />}
              {o.interestRateFloor != null && <Row label={t.floorRate} value={`${o.interestRateFloor}%`} />}
              {o.amortizationType && <Row label={t.amortization} value={amortLabel(o.amortizationType)} />}
              {o.prepaymentPenalty && <Row label={t.prepayment} value={o.prepaymentPenalty} />}
            </div>

            {/* 3. Structured Fees */}
            {(o.arrangementFeePercent != null || o.drawFeePercentABL != null || o.exitFeePercent != null || (o.feesList && o.feesList.length > 0)) && (
              <>
                <Section icon={CreditCard} title={t.sFees} />
                <div className="bg-muted/50 rounded-lg px-4 divide-y divide-border">
                  {o.arrangementFeePercent != null && <Row label={t.arrangementFee} value={`${o.arrangementFeePercent}% ${t.ofLoan}`} />}
                  {o.drawFeePercentABL != null && <Row label={t.ablDrawFee} value={`${o.drawFeePercentABL}% ${t.perDraw}`} />}
                  {o.exitFeePercent != null && <Row label={t.exitFee} value={`${o.exitFeePercent}%`} />}
                  {o.feesList && o.feesList.length > 0 && (
                    <div className="py-2">
                      <div className="text-sm text-muted-foreground mb-1">{t.additionalFees}</div>
                      {o.feesList.map((fee, i) => (
                        <div key={i} className="flex justify-between text-sm py-0.5">
                          <span>{fee.description}</span>
                          <span className="font-medium">{fee.type === 'percentage_of_loan' ? `${fee.amount}%` : fmt(fee.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* 4. Covenants, Ratios & Collateral */}
            <Section icon={Shield} title={t.sCovenants} />
            <div className="bg-muted/50 rounded-lg px-4 divide-y divide-border">
              {o.maintenanceCovenantsRequired != null && <Row label={t.maintenanceCovenants} value={bool(o.maintenanceCovenantsRequired)} />}
              {o.netDebtToEBITDAMaxAllowed != null && <Row label={t.netDebtEBITDA} value={`${o.netDebtToEBITDAMaxAllowed}x`} />}
              {o.dscrMinimumRequired != null && <Row label={t.minDSCR} value={`${o.dscrMinimumRequired}x`} />}
              {o.dscrCalculationBase && <Row label={t.dscrBase} value={o.dscrCalculationBase.toUpperCase()} />}
              {o.dividendRestrictions && <Row label={t.dividendRestrictions} value={o.dividendRestrictions} />}
              {o.collateralRequired != null && <Row label={t.collateral} value={bool(o.collateralRequired)} />}
              {o.collateralRequirements && o.collateralRequirements.length > 0 && (
                <Row label={t.collateralTypes} value={o.collateralRequirements.join(', ')} />
              )}
              {o.personalGuaranteeRequired != null && <Row label={t.personalGuarantee} value={bool(o.personalGuaranteeRequired)} />}
              {o.realEstateConditioned != null && <Row label={t.realEstateMortgage} value={bool(o.realEstateConditioned)} />}
            </div>

            {/* ABL Details */}
            {o.isABLOffer && (
              <>
                <Section icon={Building2} title={t.sABL} />
                <div className="bg-muted/50 rounded-lg px-4 divide-y divide-border">
                  {o.ABL_AR_EligiblityPercent != null && <Row label={t.ablAR} value={`${o.ABL_AR_EligiblityPercent}%`} />}
                  {o.ABL_Inventory_EligiblityPercent != null && <Row label={t.ablInventory} value={`${o.ABL_Inventory_EligiblityPercent}%`} />}
                  {o.ABL_Cash_Sweep != null && <Row label={t.ablSweep} value={bool(o.ABL_Cash_Sweep)} />}
                </div>
              </>
            )}

            {/* Subordinated Debt */}
            {o.isSubordinatedDebt && (
              <>
                <Section icon={FileText} title={t.sSubDebt} />
                <div className="bg-muted/50 rounded-lg px-4 divide-y divide-border">
                  {o.subordinationToSeniorDebt != null && <Row label={t.subToSenior} value={bool(o.subordinationToSeniorDebt)} />}
                  {o.interestGracePeriodMonths != null && <Row label={t.gracePeriod} value={`${o.interestGracePeriodMonths} ${t.months}`} />}
                  {o.balloonPaymentYear != null && <Row label={t.balloonYear} value={o.balloonPaymentYear} />}
                  {o.balloonPaymentAmount != null && <Row label={t.balloonAmount} value={fmt(o.balloonPaymentAmount)} />}
                </div>
              </>
            )}

            {/* 5. Credit Requirements & International */}
            {(o.creditScoreRequired != null || o.acceptForeignCreditScores != null || o.acceptForeignCollateral != null) && (
              <>
                <Section icon={Globe} title={t.sCredit} />
                <div className="bg-muted/50 rounded-lg px-4 divide-y divide-border">
                  {o.creditScoreRequired != null && <Row label={t.creditScore} value={bool(o.creditScoreRequired)} />}
                  {o.creditScoreMinimum != null && <Row label={t.minCreditScore} value={o.creditScoreMinimum} />}
                  {o.acceptForeignCreditScores != null && <Row label={t.foreignCredit} value={bool(o.acceptForeignCreditScores)} />}
                  {o.foreignCreditScoreMinimum != null && <Row label={t.foreignMinScore} value={o.foreignCreditScoreMinimum} />}
                  {o.acceptedForeignCreditCountries && o.acceptedForeignCreditCountries.length > 0 && (
                    <Row label={t.foreignCountries} value={o.acceptedForeignCreditCountries.join(', ')} />
                  )}
                  {o.acceptForeignCollateral != null && <Row label={t.foreignCollateral} value={bool(o.acceptForeignCollateral)} />}
                  {o.foreignCollateralRequirements && o.foreignCollateralRequirements.length > 0 && (
                    <Row label={t.foreignCollateralTypes} value={o.foreignCollateralRequirements.join(', ')} />
                  )}
                </div>
              </>
            )}

            {/* 6. Timeline */}
            {(o.estimatedClosingTimelineDays != null || o.offerExpirationDate) && (
              <>
                <Section icon={Calendar} title={t.sTimeline} />
                <div className="bg-muted/50 rounded-lg px-4 divide-y divide-border">
                  {o.estimatedClosingTimelineDays != null && (
                    <Row label={t.closingTimeline} value={`${o.estimatedClosingTimelineDays} ${t.days}`} icon={Clock} />
                  )}
                  {o.offerExpirationDate && (
                    <Row label={t.offerExpires} value={new Date(o.offerExpirationDate).toLocaleDateString()} />
                  )}
                </div>
              </>
            )}

            {/* General details fallback */}
            {o.details && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="text-sm text-muted-foreground">{o.details}</div>
              </div>
            )}

            {/* Note */}
            <p className="text-xs text-muted-foreground italic bg-blue-50 p-3 rounded-lg border border-blue-200">
              ℹ️ {t.note}
            </p>
          </div>
        </ScrollArea>

        <div className="flex gap-3 pt-2">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
            {t.close}
          </Button>
          <Button
            className="flex-1 bg-gradient-to-r from-[#b8924f] to-[#d4af6a] text-white"
            onClick={handleInterested}
          >
            <ArrowRight className="h-4 w-4 mr-2" />
            {t.interested}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
